import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import './EnvironmentToggle.css';

interface EnvironmentToggleProps {
  onEnvironmentChange?: (environment: string) => void;
}

const EnvironmentToggle: React.FC<EnvironmentToggleProps> = ({ onEnvironmentChange }) => {
  const [currentEnvironment, setCurrentEnvironment] = useState<string>('DEV');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    fetchCurrentEnvironment();
  }, []);

  const fetchCurrentEnvironment = async () => {
    try {
      const result = await apiService.getCurrentEnvironment();
      setCurrentEnvironment(result.environment);

      if (onEnvironmentChange) {
        onEnvironmentChange(result.environment);
      }
    } catch (err) {
      console.error('Failed to fetch current environment:', err);
      setError('Failed to fetch current environment');
    }
  };

  const handleEnvironmentSwitch = async (environment: string) => {
    if (environment === currentEnvironment) return;

    setIsLoading(true);
    setError('');

    try {
      const result = await apiService.switchEnvironment(environment);
      setCurrentEnvironment(result.environment);

      if (onEnvironmentChange) {
        onEnvironmentChange(result.environment);
      }

      // Show success message briefly
      setTimeout(() => setError(''), 2000);

    } catch (err) {
      console.error('Failed to switch environment:', err);
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="environment-toggle">
      <div className="environment-info">
        <span className="environment-label">Environment:</span>
        <div className="environment-buttons">
          <button
            className={`env-button ${currentEnvironment === 'DEV' ? 'active' : ''} ${isLoading ? 'loading' : ''}`}
            onClick={() => handleEnvironmentSwitch('DEV')}
            disabled={isLoading}
          >
            DEV
          </button>
          <button
            className={`env-button ${currentEnvironment === 'TEST' ? 'active' : ''} ${isLoading ? 'loading' : ''}`}
            onClick={() => handleEnvironmentSwitch('TEST')}
            disabled={isLoading}
          >
            TEST
          </button>
          <button
            className={`env-button ${currentEnvironment === 'PROD' ? 'active' : ''} ${isLoading ? 'loading' : ''}`}
            onClick={() => handleEnvironmentSwitch('PROD')}
            disabled={isLoading}
          >
            PROD
          </button>
        </div>
      </div>

      {isLoading && <div className="loading-indicator">Switching...</div>}

      {error && (
        <div className={`message ${error.includes('Failed') ? 'error' : 'success'}`}>
          {error}
        </div>
      )}
    </div>
  );
};

export default EnvironmentToggle;